import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	Button,
	ButtonProps,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	ViewableObserver,
} from "@hex-insights/core";
import { FormState } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import {
	mergeFilterInputs,
	SearchFormValues,
	UserFilterInput,
	UserNamesDescriptionsAndImagesQuery,
	useUserNamesDescriptionsAndImagesQuery,
} from "../../../../Utilities";
import { UserFilterForm, UserFilterFormState, UserFilterFormUtils } from "../../../Filter";
import { GeneralUserIconNameDescription, GeneralUserIconNameDescriptionProps } from "../../_General";

export type ControlledUsersListModalControllerProps = Omit<ButtonProps, "onClick"> &
	Pick<ControlledUsersListModalProps, "filters" | "heading">;

export function ControlledUsersListModalController({
	filters,
	heading,
	...props
}: ControlledUsersListModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button {...props} onClick={openModal} />

			<Modal.If condition={isModalOpen}>
				<ControlledUsersListModal onClose={closeModal} filters={filters} heading={heading} />
			</Modal.If>
		</React.Fragment>
	);
}

export type ControlledUsersListModalProps = Omit<
	UsersListModalProps<UserNamesDescriptionsAndImagesQuery["userConnection"]["edges"][0]["node"]>,
	"isLoading" | "users" | "filterFormState" | "hasMore" | "loadMore"
> & {
	filters?: UserFilterInput;
	pageSize?: number;
};

export function ControlledUsersListModal({ filters, pageSize = 50, ...props }: ControlledUsersListModalProps) {
	const filterFormState = UserFilterFormState.useFilterFormState();
	const filterInput = IndexForms.useFilterInput(filterFormState.formValues, UserFilterFormUtils.toFilterInputs);

	const { loading, data, fetchMore } = useUserNamesDescriptionsAndImagesQuery({
		variables: {
			filters: mergeFilterInputs(filterInput, filters),
			pagination: { first: pageSize },
		},
	});
	const endCursor = data?.userConnection.pageInfo.endCursor;
	const loadMore = React.useCallback(() => {
		fetchMore({
			variables: { pagination: { first: pageSize, after: endCursor } },
			updateQuery: (prev, { fetchMoreResult }) => ({
				...prev,
				userConnection: {
					...prev.userConnection,
					pageInfo: fetchMoreResult.userConnection.pageInfo,
					edges: [...prev.userConnection.edges, ...fetchMoreResult.userConnection.edges],
				},
			}),
		});
	}, [fetchMore, pageSize, endCursor]);
	const users = React.useMemo(() => data?.userConnection.edges.map((e) => e.node), [data]);

	return (
		<UsersListModal
			isLoading={loading}
			users={users}
			filterFormState={filterFormState}
			hasMore={data?.userConnection.pageInfo.hasNextPage}
			loadMore={loadMore}
			{...props}
		/>
	);
}

export type UsersListModalProps<T extends GeneralUserIconNameDescriptionProps["user"]> = {
	backButtonText?: string;
	heading?: JSX.Element | string;
	isLoading?: boolean;
	users: T[] | null | undefined;
	getUserDescription?: (user: T) => string | JSX.Element | null | undefined;
	filterFormState?: FormState<SearchFormValues>;
	hasMore?: boolean;
	loadMore?: () => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function UsersListModal<T extends GeneralUserIconNameDescriptionProps["user"]>({
	backButtonText,
	heading = "Users",
	isLoading = false,
	users,
	getUserDescription,
	filterFormState,
	hasMore = false,
	loadMore,
	ifRef,
	onClose,
	className,
	style,
}: UsersListModalProps<T>) {
	return (
		<Modal size="large" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ marginRight: "1.5rem" }}>
					<If condition={!!backButtonText}>
						{onClose && (
							<Button variant="link" size="small" onClick={onClose}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
									<Icon.ArrowLeft size={14} /> {backButtonText}
								</Row>
							</Button>
						)}
					</If>

					<Row justify="spaced-start" align="center">
						<Heading.H2 noMargin>{heading}</Heading.H2>
					</Row>
				</Column>
			</Modal.Header>
			<Modal.Body>
				{!!filterFormState && (
					<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ marginBottom: "1.5rem" }}>
						<UserFilterForm.Search formState={filterFormState} />
					</Row>
				)}

				<Conditional>
					<If condition={!users && isLoading}>Loading...</If>
					<Else>
						<Grid tracks="auto-fill" minTrackWidth="18rem" gap="1rem">
							{users?.map((user) => (
								<GeneralUserIconNameDescription
									key={user.id}
									user={user}
									description={getUserDescription?.(user)}
									style={{ width: "100%" }}
								/>
							))}
						</Grid>
						<If condition={hasMore && !!loadMore}>
							{!!loadMore && <ViewableObserver rootMargin="0px 0px 150px" onEnterView={loadMore} />}
						</If>
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
